import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@workspace/ui/components/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@workspace/ui/components/table";
import { CheckIcon, CopyIcon, DownloadIcon, HeartIcon, SearchIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { orpc } from "@/api/orpc";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Main } from "@/layout/main";
import { getError } from "@/utils/error";
import { fDateTime } from "@/utils/format-time";

export const Route = createFileRoute("/_authenticated/customers")({
  beforeLoad: ({ context }) => {
    if (context.dashboardRole !== "admin") throw redirect({ to: "/403", replace: true });
  },
  component: CustomersPage,
});

function CustomersPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"name" | "createdAt" | "favoritesCount">("createdAt");
  const [direction, setDirection] = useState<"asc" | "desc">("desc");
  const [status, setStatus] = useState<"all" | "active" | "deleted">("all");
  const [favoritesCustomer, setFavoritesCustomer] = useState<{ id: string; name: string }>();
  const [purgeCustomer, setPurgeCustomer] = useState<{ recordId: string; name: string }>();
  const customersQuery = useQuery(
    orpc.admin.customers.list.queryOptions({
      input: { page, perPage: 50, search: search || undefined, sort, direction, status },
    }),
  );
  const favoritesQuery = useQuery({
    ...orpc.admin.customers.favorites.queryOptions({ input: { userId: favoritesCustomer?.id ?? "" } }),
    enabled: !!favoritesCustomer,
  });
  const customersExportQuery = useQuery({
    ...orpc.admin.customers.exportCsv.queryOptions({
      input: { search: search || undefined, sort, direction, status },
    }),
    enabled: false,
  });
  const purgeDeleted = useMutation(
    orpc.admin.customers.purgeDeleted.mutationOptions({
      onSuccess: async () => {
        setPurgeCustomer(undefined);
        await Promise.all([
          customersQuery.refetch(),
          queryClient.invalidateQueries({ queryKey: orpc.admin.dashboard.summary.queryKey() }),
        ]);
        toast.success("Deleted customer record removed permanently");
      },
      onError: (error) => toast.error(getError(error, "Unable to remove deleted customer record")),
    }),
  );

  const copy = async (label: string, value: string | null | undefined) => {
    if (!value) return toast.error(`${label} is not available`);
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  };

  const exportCsv = async () => {
    const result = await customersExportQuery.refetch();
    if (result.error) {
      toast.error("Failed to export customers");
      return;
    }

    const customers = result.data ?? [];
    const rows = [
      [
        "Record ID",
        "Active user ID",
        "Former user ID",
        "Status",
        "Name",
        "Email",
        "Phone",
        "Email verified",
        "Favorites",
        "Registered at",
        "Deleted at",
      ],
      ...customers.map((customer) => [
        customer.recordId,
        customer.userId ?? "",
        customer.formerUserId ?? "",
        customer.status === "active" ? "Active" : "Deleted account",
        customer.name,
        customer.email,
        customer.phoneNumber ?? "",
        customer.emailVerified == null ? "N/A" : customer.emailVerified ? "Yes" : "No",
        customer.favoritesCount ?? "N/A",
        new Date(customer.createdAt).toISOString(),
        customer.deletedAt ? new Date(customer.deletedAt).toISOString() : "",
      ]),
    ];
    const csv = `\uFEFF${rows.map((row) => row.map(toCsvCell).join(",")).join("\r\n")}`;
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `homeland-customers-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    toast.success(`${customers.length} customers exported`);
  };

  return (
    <Main className="space-y-6">
      <div>
        <h1 className="font-semibold text-2xl">Registered customers</h1>
        <p className="mt-1 text-muted-foreground text-sm">
          Customer contact details and saved projects. Available to administrators only.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-64 flex-1 sm:max-w-sm">
          <SearchIcon className="absolute start-3 top-2.5 size-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Search name, email or phone…"
            className="ps-9"
          />
        </div>
        <Select
          value={sort}
          onValueChange={(value) => {
            setSort(value as typeof sort);
            setPage(1);
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="createdAt">Registration date</SelectItem>
            <SelectItem value="name">Name</SelectItem>
            <SelectItem value="favoritesCount">Favorites</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={status}
          onValueChange={(value) => {
            setStatus(value as typeof status);
            setPage(1);
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All accounts</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="deleted">Deleted accounts</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={direction}
          onValueChange={(value) => {
            setDirection(value as typeof direction);
            setPage(1);
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="desc">Descending</SelectItem>
            <SelectItem value="asc">Ascending</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={exportCsv} disabled={customersExportQuery.isFetching}>
          <DownloadIcon />
          {customersExportQuery.isFetching ? "Exporting…" : "Export CSV"}
        </Button>
      </div>
      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Verified</TableHead>
              <TableHead>Favorites</TableHead>
              <TableHead>Registered</TableHead>
              <TableHead>Deleted</TableHead>
              <TableHead className="w-14" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {customersQuery.data?.items.map((customer) => (
              <TableRow key={customer.recordId} className={customer.status === "deleted" ? "bg-muted/35" : undefined}>
                <TableCell className="font-medium">
                  <div>{customer.name}</div>
                  <div
                    className="mt-1 max-w-48 truncate font-mono font-normal text-[11px] text-muted-foreground"
                    title={
                      customer.status === "active" ? customer.userId || undefined : customer.formerUserId || undefined
                    }
                  >
                    ID: {customer.status === "active" ? customer.userId : customer.formerUserId}
                  </div>
                </TableCell>
                <TableCell>
                  <ContactValue value={customer.email} onCopy={() => copy("Email", customer.email)} />
                </TableCell>
                <TableCell>
                  <ContactValue
                    value={customer.phoneNumber || "—"}
                    onCopy={() => copy("Phone", customer.phoneNumber)}
                    disabled={!customer.phoneNumber}
                  />
                </TableCell>
                <TableCell>
                  <Badge variant={customer.status === "active" ? "secondary" : "outline"}>
                    {customer.status === "active" ? "Active" : "Deleted account"}
                  </Badge>
                </TableCell>
                <TableCell>
                  {customer.emailVerified == null ? (
                    <span className="text-muted-foreground">N/A</span>
                  ) : (
                    <Badge variant={customer.emailVerified ? "secondary" : "outline"}>
                      {customer.emailVerified ? (
                        <>
                          <CheckIcon /> Verified
                        </>
                      ) : (
                        "Not verified"
                      )}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {customer.userId ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (customer.userId) {
                          setFavoritesCustomer({ id: customer.userId, name: customer.name });
                        }
                      }}
                    >
                      <HeartIcon /> {customer.favoritesCount}
                    </Button>
                  ) : (
                    <span className="text-muted-foreground">N/A</span>
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap">{fDateTime(customer.createdAt)}</TableCell>
                <TableCell className="whitespace-nowrap">
                  {customer.deletedAt ? fDateTime(customer.deletedAt) : "—"}
                </TableCell>
                <TableCell>
                  {customer.status === "deleted" && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-destructive"
                      onClick={() => setPurgeCustomer({ recordId: customer.recordId, name: customer.name })}
                    >
                      <Trash2Icon className="size-4" />
                      <span className="sr-only">Permanently delete archived record</span>
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!customersQuery.isLoading && !customersQuery.data?.items.length && (
              <TableRow>
                <TableCell colSpan={9} className="h-24 text-center">
                  No registered customers found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 text-muted-foreground text-sm">
        <p>{customersQuery.data?.totalItems ?? 0} customers · 50 rows per page</p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!customersQuery.data?.hasPreviousPage}
            onClick={() => setPage((value) => value - 1)}
          >
            Previous
          </Button>
          <span>
            {customersQuery.data?.currentPage ?? page} / {Math.max(customersQuery.data?.totalPageNumbers ?? 1, 1)}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={!customersQuery.data?.hasNextPage}
            onClick={() => setPage((value) => value + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      <Dialog open={!!favoritesCustomer} onOpenChange={(open) => !open && setFavoritesCustomer(undefined)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Saved projects</DialogTitle>
            <DialogDescription>Read-only favorites for {favoritesCustomer?.name}.</DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Saved</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {favoritesQuery.data?.map((favorite) => (
                  <TableRow key={favorite.projectId}>
                    <TableCell className="font-medium">{favorite.code}</TableCell>
                    <TableCell>{favorite.title}</TableCell>
                    <TableCell className="whitespace-nowrap">{fDateTime(favorite.createdAt)}</TableCell>
                  </TableRow>
                ))}
                {!favoritesQuery.isLoading && !favoritesQuery.data?.length && (
                  <TableRow>
                    <TableCell colSpan={3} className="h-24 text-center">
                      No saved projects.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        title="Permanently delete archived customer record?"
        desc={`This removes ${purgeCustomer?.name ?? "this customer"} from the archive. This action cannot be undone.`}
        open={!!purgeCustomer}
        onOpenChange={(open) => !open && setPurgeCustomer(undefined)}
        destructive
        isLoading={purgeDeleted.isPending}
        confirmText="Delete permanently"
        handleConfirm={() => purgeCustomer && purgeDeleted.mutate({ recordId: purgeCustomer.recordId })}
      />
    </Main>
  );
}

function toCsvCell(value: string | number | boolean | null | undefined) {
  const text = value == null ? "" : String(value);
  const safeText = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return `"${safeText.replaceAll('"', '""')}"`;
}

function ContactValue({ value, onCopy, disabled }: { value: string; onCopy: () => void; disabled?: boolean }) {
  return (
    <div className="flex items-center gap-1">
      <span>{value}</span>
      <Button type="button" variant="ghost" size="icon" className="size-7" disabled={disabled} onClick={onCopy}>
        <CopyIcon className="size-3.5" />
        <span className="sr-only">Copy</span>
      </Button>
    </div>
  );
}
